import { useState, FC } from "react";
import { StyleSheet, View } from "react-native";
import EnrollmentLinkManager from "../../../components/adminDashboard/EnrollmentLinkManager/EnrollmentLinkManager";
import ConfirmationModal from "../../../components/general/ConfirmationModal/ConfirmationModal";
import CustomAdminModal from "../../../components/common/general/CustomAdminModal/CustomAdminModal";
import CustomText from "../../../components/common/general/CustomText/CustomText";
import CustomButton from "../../../components/common/general/CustomButton/CustomButton";
import CustomSpacing from "../../../components/common/layout/CustomSpacing/CustomSpacing";
import { useForm, Controller } from "react-hook-form";
import CustomTextInput from "../../../components/common/general/CustomTextInput/CustomTextInput";

import {
  useCreateEnrollmentLinkMutation,
  useDeleteEnrollmentLinkMutation,
  useGetEnrollmentLinkQuery,
  useUpdateEnrollmentLinkMutation,
} from "../../../graphql/generated/graphql";

const DEFAULT_EXPIRES_IN_DAYS = 60;

interface EnrollmentLinkManagerProps {
  courseId: string;
}

enum ModalMode {
  HIDDEN = "hidden",
  CREATE = "create",
  UPDATE = "update",
  DELETE = "delete",
  ERROR = "error",
  SUCCESS = "success",
}

const EnrollmentLinkManagerContainer: FC<EnrollmentLinkManagerProps> = ({ courseId }) => {
  const [modalMode, setModalMode] = useState<ModalMode>(ModalMode.HIDDEN);
  const [successMessage, setSuccessMessage] = useState("");
  const [formErrors, setFormErrors] = useState<{
    expiresInDays?: string;
    general?: string;
  }>({});

  type FormValues = {
    maxEnrollments: number | null;
    expiresInDays: number;
  };

  const {
    handleSubmit: formHandleSubmit,
    reset,
    control,
    formState: { isSubmitting },
  } = useForm<FormValues>({
    defaultValues: {
      maxEnrollments: null,
      expiresInDays: DEFAULT_EXPIRES_IN_DAYS,
    },
  });

  const {
    data: enrollmentLinkData,
    loading: enrollmentLinkLoading,
    refetch: refetchEnrollmentLink,
  } = useGetEnrollmentLinkQuery({
    variables: { courseId },
    skip: !courseId,
  });

  const enrollmentLink = enrollmentLinkData?.getEnrollmentLink?.data;

  const [createEnrollmentLink] = useCreateEnrollmentLinkMutation();
  const [updateEnrollmentLink] = useUpdateEnrollmentLinkMutation();
  const [deleteEnrollmentLink] = useDeleteEnrollmentLinkMutation();

  const handleCreateLink = () => {
    setModalMode(ModalMode.CREATE);
    reset({ maxEnrollments: null, expiresInDays: DEFAULT_EXPIRES_IN_DAYS });
    setFormErrors({});
  };

  const handleUpdateLink = () => {
    setModalMode(ModalMode.UPDATE);
    const existingMax = enrollmentLinkData?.getEnrollmentLink?.data?.maxEnrollments || null;
    let days = DEFAULT_EXPIRES_IN_DAYS;
    if (enrollmentLink?.expiresAt) {
      const now = new Date();
      const expiresAt = new Date(enrollmentLink.expiresAt);
      const remainingDays = Math.ceil((expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      days = remainingDays <= 0 ? DEFAULT_EXPIRES_IN_DAYS : remainingDays;
    }
    reset({ maxEnrollments: existingMax, expiresInDays: days });
    setFormErrors({});
  };

  const onSubmit = formHandleSubmit(async (values: FormValues) => {
    const { maxEnrollments, expiresInDays } = values;
    setFormErrors({});

    if (!courseId) {
      setFormErrors({ general: "Course ID is required" });
      return;
    }

    if (!expiresInDays || expiresInDays <= 0) {
      setFormErrors({ expiresInDays: "Expiration days must be greater than 0" });
      return;
    }

    if (expiresInDays > 60) {
      setFormErrors({ expiresInDays: "Expiration days cannot exceed 60 days" });
      return;
    }

    try {
      if (modalMode === ModalMode.CREATE) {
        const response = await createEnrollmentLink({
          variables: {
            input: {
              courseId,
              maxEnrollments: maxEnrollments || null,
              expiresInDays: expiresInDays,
            },
          },
        });

        if (response.data?.createEnrollmentLink?.success) {
          setSuccessMessage("Enrollment link generated successfully!");
          setModalMode(ModalMode.SUCCESS);
          refetchEnrollmentLink();
        } else {
          setFormErrors({
            general: response.data?.createEnrollmentLink?.message || "Failed to generate enrollment link",
          });
          setModalMode(ModalMode.ERROR);
        }
      } else {
        if (!enrollmentLink?.id) {
          setFormErrors({ general: "Update functionality needs enrollment link ID" });
          setModalMode(ModalMode.ERROR);
          return;
        }

        const response = await updateEnrollmentLink({
          variables: {
            id: enrollmentLink.id,
            input: {
              maxEnrollments: maxEnrollments || null,
              expiresInDays: expiresInDays,
            },
          },
        });

        if (response.data?.updateEnrollmentLink?.success) {
          setSuccessMessage("Enrollment link updated successfully!");
          setModalMode(ModalMode.SUCCESS);
          refetchEnrollmentLink();
        } else {
          setFormErrors({
            general: response.data?.updateEnrollmentLink?.message || "Failed to update enrollment link",
          });
          setModalMode(ModalMode.ERROR);
        }
      }
    } catch (error) {
      setFormErrors({ general: `Failed to ${modalMode === ModalMode.CREATE ? "create" : "update"} enrollment link` });
      setModalMode(ModalMode.ERROR);
    }
  });

  const handleDeleteLink = async () => {
    try {
      await deleteEnrollmentLink({ variables: { id: enrollmentLinkData?.getEnrollmentLink?.data?.id } });
      refetchEnrollmentLink();
      setSuccessMessage("Enrollment link deleted successfully!");
      setModalMode(ModalMode.SUCCESS);
    } catch (error) {
      setFormErrors({ general: "Failed to delete enrollment link" });
      setModalMode(ModalMode.ERROR);
    }
  };

  return (
    <>
      <EnrollmentLinkManager
        enrollmentLink={enrollmentLinkData?.getEnrollmentLink?.data}
        handleCreateLink={handleCreateLink}
        handleUpdateLink={handleUpdateLink}
        handleDeleteLink={() => setModalMode(ModalMode.DELETE)}
        isLoading={enrollmentLinkLoading}
      />
      <CustomAdminModal
        visible={modalMode === ModalMode.CREATE || modalMode === ModalMode.UPDATE}
        onRequestClose={() => setModalMode(ModalMode.HIDDEN)}
        title={modalMode === ModalMode.UPDATE ? "Update Course Enrollment Link" : "Generate Course Enrollment Link"}
        ariaLabel={
          modalMode === ModalMode.UPDATE
            ? "Update course enrollment link modal"
            : "Generate course enrollment link modal"
        }
        style={styles.modal}>
        <View style={styles.container}>
          <CustomSpacing type="vertical" size="l" />
          <View style={styles.inputContainer}>
            <CustomText
              text="Maximum number of enrollments"
              size="m"
              weight="medium"
              useTranslationText={false}
              style={styles.label}
            />
            <CustomText
              text="(Optional - leave empty for unlimited enrollments)"
              size="s"
              weight="regular"
              useTranslationText={false}
              style={styles.helperText}
            />
            <CustomSpacing type="vertical" size="s" />
            <Controller
              control={control}
              name="maxEnrollments"
              render={({ field }) => (
                <CustomTextInput
                  value={field.value != null ? String(field.value) : ""}
                  onChangeText={(value) => field.onChange(value ? parseInt(value, 10) : null)}
                  placeholder="Enter number of enrollments"
                  keyboardType="numeric"
                  style={styles.input}
                />
              )}
            />
          </View>
          <CustomSpacing type="vertical" size="m" />
          <View style={styles.inputContainer}>
            <CustomText
              text="Expiration days"
              size="m"
              weight="medium"
              useTranslationText={false}
              style={styles.label}
            />
            <CustomText
              text="(Required - maximum 60 days)"
              size="s"
              weight="regular"
              useTranslationText={false}
              style={styles.helperText}
            />
            <CustomSpacing type="vertical" size="s" />
            <Controller
              control={control}
              name="expiresInDays"
              render={({ field }) => (
                <CustomTextInput
                  value={String(field.value ?? "")}
                  onChangeText={(value) => field.onChange(value ? parseInt(value, 10) : "")}
                  placeholder="Enter number of days (1-60)"
                  keyboardType="numeric"
                  style={styles.input}
                  maxLength={2}
                />
              )}
            />
            {formErrors.expiresInDays && (
              <CustomText
                text={formErrors.expiresInDays}
                size="s"
                style={styles.errorText}
                useTranslationText={false}
              />
            )}
          </View>
          {formErrors.general && (
            <CustomText text={formErrors.general} size="s" style={styles.errorText} useTranslationText={false} />
          )}
          <CustomSpacing type="vertical" size="l" />
          <CustomButton
            text={
              isSubmitting
                ? modalMode === ModalMode.UPDATE
                  ? "Updating..."
                  : "Generating..."
                : modalMode === ModalMode.UPDATE
                  ? "Update Link"
                  : "Generate Link"
            }
            styleType="primary"
            size="m"
            bold={true}
            onPress={onSubmit}
            useTranslationText={false}
            disabled={isSubmitting}
          />
          <CustomSpacing type="vertical" size="l" />
        </View>
      </CustomAdminModal>
      <ConfirmationModal
        visible={modalMode === ModalMode.DELETE}
        onClose={() => setModalMode(ModalMode.HIDDEN)}
        onConfirm={handleDeleteLink}
        message="Are you sure you want to delete this enrollment link? This action cannot be undone."
      />
      <CustomAdminModal
        visible={modalMode === ModalMode.ERROR}
        onRequestClose={() => setModalMode(ModalMode.HIDDEN)}
        title="Error"
        ariaLabel="Error modal"
        style={{}}>
        <View style={{ padding: 24 }}>
          <CustomText
            text={formErrors.general || "An error occurred"}
            size="m"
            style={{ color: "#DC2626", textAlign: "center" }}
          />
          <CustomSpacing type="vertical" size="l" />
          <CustomButton
            text="Close"
            onPress={() => setModalMode(ModalMode.HIDDEN)}
            styleType="primary"
            style={{ width: "100%" }}
          />
        </View>
      </CustomAdminModal>
      <CustomAdminModal
        visible={modalMode === ModalMode.SUCCESS}
        onRequestClose={() => setModalMode(ModalMode.HIDDEN)}
        title="Success"
        ariaLabel="Success modal"
        style={{}}>
        <View style={{ padding: 24 }}>
          <CustomText text={successMessage} size="m" style={{ color: "#059669", textAlign: "center" }} />
          <CustomSpacing type="vertical" size="l" />
          <CustomButton
            text="Close"
            onPress={() => setModalMode(ModalMode.HIDDEN)}
            styleType="primary"
            style={{ width: "100%" }}
          />
        </View>
      </CustomAdminModal>
    </>
  );
};

export default EnrollmentLinkManagerContainer;

const styles = StyleSheet.create({
  container: {
    padding: 24,
  },
  inputContainer: {
    marginBottom: 16,
  },
  errorText: {
    color: "#DC2626",
    marginBottom: 4,
  },
  label: {
    color: "#374151",
    marginBottom: 4,
  },
  helperText: {
    color: "#6B7280",
    fontStyle: "italic",
  },
  input: {
    borderWidth: 1,
    borderColor: "#D1D5DB",
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    fontSize: 16,
  },
  modal: {
    padding: 0,
  },
});
